import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { IDS } from "@/lib/testIds";
import { Package } from "@phosphor-icons/react";

export default function Login() {
  const { login, demoLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const tryDemo = async () => {
    setErr("");
    setDemoLoading(true);
    try {
      await demoLogin();
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Left: image with heavy overlay (desktop only) */}
      <div
        className="hidden md:block md:w-1/2 relative border-r border-border"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,.72), rgba(0,0,0,.85)), url(https://images.unsplash.com/photo-1553413077-190dd305871c?w=1600)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-10 flex flex-col justify-between">
          <div className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 grid place-items-center bg-[hsl(24_100%_50%)] text-black">
              <Package size={22} weight="bold" />
            </div>
            <span className="font-display font-black text-xl tracking-tight">INVENTORY MANAGEMENT SYSTEM</span>
          </div>
          <div className="text-white max-w-md">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">Inventory Management System</div>
            <h1 className="font-display font-black text-4xl leading-[1.05] tracking-tight">
              Count. Reorder.<br />
              <span className="text-[hsl(24_100%_50%)]">Never run out.</span>
            </h1>
            <p className="text-white/70 text-sm mt-4 max-w-sm">
              A rugged inventory command center for dry-goods stockroom operations. Offline-first. QR-first. Zero-guess reorder recommendations.
            </p>
          </div>
          <div className="flex gap-6 text-[10px] uppercase tracking-[0.3em] text-white/50">
            <span>PWA</span>
            <span>Offline-First</span>
            <span>Role-Based</span>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="flex items-center gap-2 md:hidden mb-8">
            <div className="w-9 h-9 grid place-items-center bg-[hsl(24_100%_50%)] text-black">
              <Package size={22} weight="bold" />
            </div>
            <span className="font-display font-black text-xl tracking-tight">INVENTORY MANAGEMENT SYSTEM</span>
          </div>
          <div className="label-tech mb-2">Access Terminal</div>
          <h2 className="font-display font-black text-3xl tracking-tight mb-8">Sign in to continue</h2>

          <label className="label-tech block mb-1">Email</label>
          <input
            data-testid={IDS.loginEmail}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-background rounded-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm mb-4"
            required
          />
          <label className="label-tech block mb-1">Password</label>
          <input
            data-testid={IDS.loginPassword}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-background rounded-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm mb-6"
            required
          />

          {err && (
            <div
              data-testid={IDS.loginError}
              className="mb-4 border border-destructive/60 bg-destructive/10 text-destructive px-3 py-2 text-sm rounded-sm"
            >
              {err}
            </div>
          )}

          <button
            data-testid={IDS.loginSubmit}
            disabled={loading || demoLoading}
            className="btn-hard w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-60"
          >
            {loading ? "Authenticating…" : "Sign In →"}
          </button>

          <div className="my-4 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            data-testid="login-try-demo"
            onClick={tryDemo}
            disabled={loading || demoLoading}
            className="btn-hard w-full h-12 border border-primary/70 text-primary font-bold uppercase tracking-widest text-sm rounded-sm hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
          >
            {demoLoading ? "Loading demo…" : "Try Demo · Read Only"}
          </button>

          <div className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground text-center">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">Inventory Management System</div>
          </div>
        </form>
      </div>
    </div>
  );
}
